'use client';
import Link from 'next/link';
import { hasCldImage, TypeBlockIndustryBlocks, TypePageIndustry } from '@fm-nx/contentful-types';
import HeaderGroup from '../header-group/header-group';
import { CldImage } from 'next-cloudinary';
import DarkCircleBackground from '../dark-circle-background/dark-circle-background';

type IndustryBlockProps = {
    page: TypePageIndustry<'WITHOUT_UNRESOLVABLE_LINKS'>;
};

const IndustryBlock = (props: IndustryBlockProps) => {
    const { page } = props;
    const { fields } = page;
    const imageCloudinary = hasCldImage(fields.listImage?.fields) ? fields.listImage?.fields.cloudinaryMedia[0] : null;

    if (!page) return null;

    return (
        <Link
            className="mb-3 text-xl text-center text-white xl:text-2-5xl font-headline group"
            href={`/industries/${page.fields?.slug}`}
        >
            <div
                key={`industry-block-${page.sys.id}`}
                className="relative flex items-center justify-center w-full min-h-[200px] md:min-h-[300px] lg:min-h-[400px]"
            >
                {imageCloudinary && (
                    <CldImage
                        className="z-0 object-cover transition opacity-70 group-hover:opacity-25 aspect-square min-h-[200px] md:min-h-[300px] lg:min-h-[400px]"
                        src={`${imageCloudinary.public_id}`}
                        fill
                        alt={page.fields.title || 'Industry image'}
                        format="auto" // Automatically use WebP or AVIF based on browser support
                        quality="auto" // Automatically adjust quality based on network and device
                        dpr="auto" // Automatically adjust for device pixel ratio
                        loading="lazy"
                        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                        placeholder="blur"
                        blurDataURL="data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIiB2aWV3Qm94PSIwIDAgMTAwIDEwMCI+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0iIzMzMzMzMyIvPjwvc3ZnPg=="
                    />
                )}
                <div className="relative z-10 border-b-out after:bg-white">{page.fields.title}</div>
            </div>
        </Link>
    );
};

type IndustryBlocksProps = {
    blockIndustryBlocks: TypeBlockIndustryBlocks<'WITHOUT_UNRESOLVABLE_LINKS'>;
};

export const IndustryBlocks = (props: IndustryBlocksProps) => {
    const { blockIndustryBlocks } = props;
    const { fields } = blockIndustryBlocks;
    if (!fields || !fields.pages) return null;

    const { overline, headerText, paragraph } = fields;

    return (
        <DarkCircleBackground>
            <div className="mx-6 mb-0 md:mb-20 md:mx-0 md:grid grid-cols-14">
                <div className="col-span-12 col-start-2">
                    <HeaderGroup mode="dark" overline={overline} headerText={headerText} paragraph={paragraph} />

                    <div className="grid grid-cols-1 gap-6 my-10 mt-20 md:grid-cols-2 lg:grid-cols-3 md:gap-6">
                        {fields.pages.map((page) => {
                            if (!page) return null;
                            return <IndustryBlock key={`industry-block-${page.sys.id}`} page={page} />;
                        })}
                    </div>
                </div>
            </div>
        </DarkCircleBackground>
    );
};

export default IndustryBlocks;
